import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { useToast } from '../hooks/use-toast';
import LoadingSpinner from '../components/LoadingSpinner';
import StripePaymentForm from '../components/StripePaymentForm';
import ImageUpload from '../components/ImageUpload';
import { Calendar as CalendarIcon, Car, MapPin, Gift } from 'lucide-react';
import { Switch } from '../components/ui/switch';
import { format } from 'date-fns';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const Booking = () => {
  const { carId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [car, setCar] = useState(null);
  const [startDate, setStartDate] = useState();
  const [endDate, setEndDate] = useState();
  const [licenseImages, setLicenseImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPayment, setShowPayment] = useState(false);
  const [creditBalance, setCreditBalance] = useState(0);
  const [applyCredit, setApplyCredit] = useState(false);

  useEffect(() => {
    const fetchCar = async () => {
      try {
        const { carsAPI, referralsAPI } = await import('../api');
        const [carRes, refRes] = await Promise.all([
          carsAPI.getById(carId),
          referralsAPI.me().catch(() => ({ data: { credit_balance: 0 } })),
        ]);
        setCar(carRes.data);
        setCreditBalance(refRes.data.credit_balance || 0);
      } catch (error) {
        console.error('Error fetching car:', error);
        toast({
          title: 'Error',
          description: 'No se pudo cargar el auto',
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    };
    
    fetchCar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carId]);

  if (!user) {
    navigate('/login', { state: { from: `/booking/${carId}` } });
    return null;
  }

  const calculateDays = () => {
    if (!startDate || !endDate) return 0;
    const diffTime = Math.abs(endDate - startDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays || 1;
  };

  const calculateTotal = () => {
    if (!car) return 0;
    return car.price_per_day * calculateDays();
  };

  const creditApplied = applyCredit ? Math.min(creditBalance, calculateTotal()) : 0;
  const finalTotal = Math.max(0, calculateTotal() - creditApplied);

  const handleProceedToPayment = () => {
    if (!startDate || !endDate) {
      toast({
        title: 'Error',
        description: 'Selecciona las fechas de tu reserva',
        variant: 'destructive',
      });
      return;
    }

    if (licenseImages.length < 2) {
      toast({
        title: 'Error',
        description: 'Sube fotos de ambos lados de tu licencia',
        variant: 'destructive',
      });
      return;
    }

    setShowPayment(true);
  };

  const handlePaymentSuccess = async (paymentMethodId) => {
    setLoading(true);
    try {
      const { bookingsAPI } = await import('../api');
      
      const bookingData = {
        car_id: car.id,
        start_date: format(startDate, 'yyyy-MM-dd'),
        end_date: format(endDate, 'yyyy-MM-dd'),
        total_price: calculateTotal(),
        days: calculateDays(),
        license_images: licenseImages,
        payment_method_id: paymentMethodId
      };

      await bookingsAPI.create(bookingData);

      toast({
        title: '¡Reserva creada!',
        description: 'El Mobilitier revisará tu solicitud. Se ha autorizado el pago.',
      });

      navigate('/dashboard');
    } catch (error) {
      console.error('Error creating booking:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Error al crear la reserva',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="xl" />
      </div>
    );
  }

  if (!car) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold">Auto no encontrado</h2>
          <Button onClick={() => navigate('/search')} className="mt-4">
            Volver a búsqueda
          </Button>
        </div>
      </div>
    );
  }

  const days = calculateDays();
  const total = calculateTotal();
  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4">
        <div className="mb-6">
          <h1 className="text-3xl font-bold">Solicitar Reserva</h1>
          <p className="text-gray-600 mt-2">Completa los detalles de tu viaje</p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Left Column - Booking Form */}
          <div className="space-y-6">
            {/* Car Info */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Car className="w-5 h-5" />
                  Auto Seleccionado
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex gap-4">
                  {car.images && car.images[0] && (
                    <img
                      src={car.images[0]}
                      alt={car.name}
                      className="w-24 h-20 object-cover rounded-lg"
                    />
                  )}
                  <div>
                    <h3 className="font-semibold text-lg">{car.name}</h3>
                    <div className="flex items-center gap-2 text-gray-600 text-sm mt-1">
                      <MapPin className="w-4 h-4" />
                      {car.location}
                    </div>
                    <p className="text-blue-600 font-bold mt-1">
                      ${car.price_per_day} MXN/día
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Dates */}
            <Card>
              <CardHeader>
                <CardTitle>Fechas del Viaje</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium">Inicio</label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full justify-start text-left font-normal mt-1"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {startDate ? format(startDate, 'PPP') : 'Seleccionar'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={startDate}
                          onSelect={setStartDate}
                          disabled={(date) => date < new Date()}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>

                  <div>
                    <label className="text-sm font-medium">Fin</label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full justify-start text-left font-normal mt-1"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {endDate ? format(endDate, 'PPP') : 'Seleccionar'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={endDate}
                          onSelect={setEndDate}
                          disabled={(date) => date < (startDate || new Date())}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>

                {days > 0 && (
                  <div className="bg-blue-50 p-3 rounded-lg">
                    <p className="text-sm text-blue-800">
                      <span className="font-semibold">{days} días</span> de renta
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* License Upload */}
            {!showPayment && (
              <Card>
                <CardHeader>
                  <CardTitle>Licencia de Conducir</CardTitle>
                  <p className="text-sm text-gray-600">
                    Sube fotos de ambos lados de tu licencia (frontal y reverso)
                  </p>
                </CardHeader>
                <CardContent>
                  <ImageUpload
                    maxFiles={2}
                    onUploadComplete={setLicenseImages}
                    folder="licenses"
                    existingImages={licenseImages}
                  />
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right Column - Payment / Summary */}
          <div>
            {!showPayment ? (
              <Card>
                <CardHeader>
                  <CardTitle>Resumen</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Precio por día</span>
                      <span>${car.price_per_day} MXN</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Días</span>
                      <span>{days || 0}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Subtotal</span>
                      <span>${total} MXN</span>
                    </div>
                    {creditApplied > 0 && (
                      <div className="flex justify-between text-sm text-purple-700">
                        <span>Crédito aplicado</span>
                        <span>−${creditApplied} MXN</span>
                      </div>
                    )}
                    <div className="border-t pt-2 flex justify-between font-bold text-lg">
                      <span>Total a pagar</span>
                      <span className="text-blue-600" data-testid="booking-final-total">${finalTotal} MXN</span>
                    </div>
                  </div>

                  {creditBalance > 0 && days > 0 && (
                    <div className="bg-purple-50 border border-purple-200 p-3 rounded-lg" data-testid="credit-toggle-block">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Gift className="w-4 h-4 text-purple-600" />
                          <div>
                            <p className="text-sm font-medium text-purple-900">Tu crédito: ${creditBalance} MXN</p>
                            <p className="text-xs text-purple-700">{applyCredit ? `Aplicarás $${creditApplied} a esta reserva` : 'Aplica al total'}</p>
                          </div>
                        </div>
                        <Switch
                          checked={applyCredit}
                          onCheckedChange={setApplyCredit}
                          data-testid="apply-credit-switch"
                        />
                      </div>
                    </div>
                  )}

                  <div className="bg-yellow-50 p-3 rounded-lg">
                    <p className="text-sm text-yellow-800">
                      <strong>Nota:</strong> Se realizará una autorización de pago (hold) 
                      que solo se capturará si el Mobilitier aprueba tu reserva.
                    </p>
                  </div>

                  <Button
                    onClick={handleProceedToPayment}
                    className="w-full bg-[#000000] hover:bg-ink-700"
                    size="lg"
                  >
                    Continuar al Pago
                  </Button>

                  <Button
                    variant="outline"
                    onClick={() => navigate(`/car/${carId}`)}
                    className="w-full"
                  >
                    Volver
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <Elements stripe={stripePromise}>
                <StripePaymentForm
                  amount={finalTotal}
                  onSuccess={handlePaymentSuccess}
                  onCancel={() => setShowPayment(false)}
                  bookingDetails={{
                    carName: car.name,
                    dates: `${startDate ? format(startDate, 'dd/MM/yyyy') : ''} - ${endDate ? format(endDate, 'dd/MM/yyyy') : ''}`,
                    days: days
                  }}
                />
              </Elements>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Booking;
